import { useTranslate } from "@tolgee/react";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { cn } from "#/lib/utils";
import { useTradeMarket } from "../../hooks/use-trade-data.ts";
import { getMarketLogoAlt } from "../../lib/market-display.ts";
import { PerpMarketSearchDialog } from "../market-info/perp-market-search-dialog.tsx";
import { useCoinLogo } from "../market-info/use-coin-logo.ts";
import { useMarketInfo } from "../market-info/use-market-info.tsx";

export function MobileMarketHeader() {
	const { t } = useTranslate();
	const [expanded, setExpanded] = useState(false);
	const { marketData } = useTradeMarket();
	const info = useMarketInfo(marketData);
	const logo = useCoinLogo(marketData.coin, marketData.dex);

	const markMetric = info.metrics.find((m) => m.id === "mark");
	const changeMetric = info.metrics.find((m) => m.id === "change24h");
	const expandedMetrics = info.metrics.filter(
		(m) => m.id !== "mark" && m.id !== "change24h",
	);
	const leverageLabel =
		marketData.maxLeverage > 0 ? `${marketData.maxLeverage}x` : "-";

	return (
		<div className="shrink-0 border-b border-trade-border-subtle bg-trade-surface">
			{/* Collapsed header row */}
			<div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-3 py-2.5">
				<PerpMarketSearchDialog
					currentCoin={marketData.coin}
					trigger={
						<button
							type="button"
							className="flex min-w-0 cursor-pointer items-center gap-2"
						>
							{logo.logoFailed ? (
								<div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-trade-interactive text-caption1-medium text-trade-text-strong">
									{info.symbolBadgeLabel}
								</div>
							) : (
								<img
									src={logo.logoSrc}
									alt={getMarketLogoAlt({
										coin: marketData.coin,
										dex: marketData.dex,
										t,
									})}
									className="h-6 w-6 shrink-0 rounded-full"
									onError={logo.onLogoError}
								/>
							)}
							<div className="min-w-0">
								<div className="flex min-w-0 items-center gap-1.5">
									<span className="truncate text-headline text-trade-text-strong">
										{info.symbolLabel}
									</span>
									<span className="shrink-0 text-caption1-medium text-trade-accent">
										{leverageLabel}
									</span>
									<ChevronDown className="h-4 w-4 shrink-0 text-trade-text-secondary" />
								</div>
							</div>
						</button>
					}
				/>

				<div className="min-w-[94px] text-right">
					<div className="truncate text-headline tabular-nums text-trade-text-strong">
						{markMetric?.value ?? "-"}
					</div>
					<div
						className={cn(
							"truncate text-label2-medium tabular-nums",
							changeMetric?.valueClassName ?? "text-trade-text",
						)}
					>
						{changeMetric?.value ?? "-"}
					</div>
				</div>

				<button
					type="button"
					onClick={() => setExpanded((prev) => !prev)}
					className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[8px] border border-trade-border-subtle text-trade-text-secondary"
					aria-label={expanded ? "Collapse market info" : "Expand market info"}
				>
					<ChevronDown
						className={cn(
							"h-5 w-5 transition-transform duration-200",
							expanded && "rotate-180",
						)}
					/>
				</button>
			</div>

			{/* Expanded metrics */}
			<div
				className={cn(
					"grid transition-[grid-template-rows] duration-200 ease-out",
					expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
				)}
			>
				<div className="overflow-hidden">
					<div className="grid grid-cols-2 gap-x-5 gap-y-2.5 px-3 pb-3">
						{expandedMetrics.map((metric) => (
							<div key={metric.id} className="min-w-0">
								<div className="truncate text-caption1-medium text-trade-text-faint">
									{t(metric.label)}
								</div>
								<div
									className={cn(
										"mt-0.5 truncate text-label1-bold",
										metric.valueClassName ?? "text-trade-text",
									)}
								>
									{metric.value}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
